import type { Organization } from "api/organizations.types";
import type { LoggedInUser } from "api/User.types";
import type { StreamFilters } from "pages/index/components/lists/streamFilters/useStreamFilters";

//JSON serializable value
type ParameterBaseValue = string | number | boolean | null | undefined;
type ParameterValue =
    | ParameterBaseValue
    | Record<string, ParameterBaseValue>
    | ParameterBaseValue[];
type AnalyticsParameters = Record<string, ParameterValue>;

export const GoogleAnalytics = {
    isAvailable() {
        return (
            typeof window !== "undefined" &&
            typeof window.gtag !== "undefined" &&
            window.gtag
        );
    },
    setUserId(userId: string) {
        if (!GoogleAnalytics.isAvailable()) {
            return;
        }
        window.gtag("set", { userId });
    },

    trackEvent(eventName: string, eventParameters: { [key: string]: unknown }) {
        if (!GoogleAnalytics.isAvailable()) {
            return;
        }
        window.gtag("event", eventName, eventParameters);
    }
};

export const HeapAnalytics = {
    identify(organization: Organization, user: LoggedInUser) {
        if (typeof window !== `undefined` && window.heap) {
            if (window.heap.userId === user.userId) {
                return;
            }

            window.heap.identify(user.userId);
            window.heap.addUserProperties({
                Name: user.name,
                Role: user.role,
                Email: user.email,
                "Organization ID": organization.organizationId,
                "Organization Name": organization.name,
                "Organization Status": organization.status
            });
        }
    },
    getUserId() {
        if (!HeapAnalytics.isDefined()) {
            return;
        }
        return window.heap.getIdentity();
    },

    isDefined() {
        return typeof window !== `undefined` && !!window.heap;
    },

    resetIdentity() {
        if (!HeapAnalytics.isDefined()) {
            return;
        }
        window.heap.resetIdentity();
    },

    trackEvent(name: string, data: AnalyticsParameters) {
        if (typeof window !== `undefined` && window.heap) {
            window.heap.track(name, data);
        }
    }
};

/*******************************************
 * Public Methods (Core Basic Methods)
 *******************************************/

export const trackEvent = (
    category: string,
    eventName: string,
    extraData: AnalyticsParameters = {}
) => {
    GoogleAnalytics.trackEvent(eventName, { category, ...extraData });

    // Doing some parsing since heap does not support 'category AND event' so merging category:event
    HeapAnalytics.trackEvent(`${category}:${eventName}`, extraData);
};

/*******************************************
 * Public Methods (Helper / Specific Methods)
 *******************************************/

/*
 * Analytics Tracking
 */

export const trackOptGroupSelectizeSelection = (filter: string) =>
    trackEvent(`studio:analytics`, `selectize-filter`, {
        filter
    });

export const trackDateFilterSelection = (filter: string) => {
    trackEvent(`studio:analytics`, `date-filter`, {
        filter
    });
};

export const trackDateFilterQuarterSelection = () =>
    trackEvent(`studio:analytics`, `date-filter-quarterly`);

export const trackDateFilterYearSelection = () =>
    trackEvent(`studio:analytics`, `date-filter-yearly`);

export const trackTopClipsSortStrategySelection = (sortStrategy: string) =>
    trackEvent(`studio:analytics`, `top-clips`, {
        "sort-strategy": sortStrategy
    });

export const trackClipDetailsSelection = (shareId: string) =>
    trackEvent(`studio:analytics`, `clip-details-open`, {
        "share-id": shareId
    });

export const trackClipDetailsTabSelection = (tab: string) =>
    trackEvent(`studio:analytics`, `clip-details-tab`, {
        tab
    });

export const trackBackFaceSelection = (cardTitle: string) =>
    trackEvent(`studio:analytics`, `backface`, {
        "card-title": cardTitle
    });

export const trackExpandCard = (cardTitle: string) =>
    trackEvent(`studio:analytics`, `expand-card`, {
        "card-title": cardTitle
    });

export const trackChangeTimeGranularity = (granularity: string) =>
    trackEvent(`studio:analytics`, `card-filter-day`, {
        granularity
    });

/*
 * Live trimming tracking
 */

export const trackTrimPreviewSelection = () =>
    trackEvent(`studio:trim`, `preview`);

export const trackTopControlsButtonTrimPreview = () =>
    trackEvent(`studio:trim`, `preview-topcontrols-btn`);

export const trackTrimShareDvr = (data: AnalyticsParameters) =>
    trackEvent(
        `studio:trim`,
        data.hasCreativeSet ? `share-with-creative-set` : `share`,
        {
            "input-type": `dvr`,
            "grab-type": data.grabType,
            grabId: data.grabId,
            socialAccounts: data.socialAccounts,
            scheduled: data.scheduled,
            "youtube-content-owner-id": data.youtubeContentOwnerId,
            "youtube-policy-id": data.youtubePolicyId
        }
    );

export const trackTrimShareVod = (data: AnalyticsParameters) =>
    trackEvent(
        `studio:trim`,
        data.hasCreativeSet ? `share-with-creative-set` : `share`,
        {
            "input-type": `vod`,
            "grab-type": data.grabType,
            grabId: data.grabId,
            socialAccounts: data.socialAccounts,
            scheduled: data.scheduled,
            "youtube-content-owner-id": data.youtubeContentOwnerId,
            "youtube-policy-id": data.youtubePolicyId
        }
    );

export const trackClipCreatedDvr = (
    inputStreamId: string,
    outputGrabId: string,
    grabType: string,
    hasCreativeSet: boolean
) =>
    trackEvent(
        `studio:trim`,
        hasCreativeSet ? `save-with-creative-set` : `save`,
        {
            "input-type": `dvr`,
            "input-stream-id": inputStreamId,
            "output-grab-type": grabType,
            "output-grab-id": outputGrabId
        }
    );

export const trackClipCreatedVod = (
    inputGrabId: string,
    outputGrabId: string,
    grabType: string,
    hasCreativeSet: boolean
) =>
    trackEvent(
        `studio:trim`,
        hasCreativeSet ? `save-with-creative-set` : `save`,
        {
            "input-type": `vod`,
            "input-grab-id": inputGrabId,
            "output-grab-type": grabType,
            "output-grab-id": outputGrabId
        }
    );

export const trackEditorExport = (
    compositionId: string,
    grabType: string,
    outputGrabId: string
) =>
    trackEvent(`studio:editor`, `export`, {
        compositionId,
        "output-grab-type": grabType,
        "output-grab-id": outputGrabId
    });

export const trackEditorShare = (
    compositionId: string,
    grabId: string,
    socialAccounts: string[],
    scheduled: boolean,
    hasCreativeSet: boolean
) =>
    trackEvent(
        `studio:editor`,
        hasCreativeSet ? `share-with-creative-set` : `share`,
        {
            compositionId,
            grabId,
            socialAccounts,
            scheduled
        }
    );

export const trackEditorCreateProject = (id: string) =>
    trackEvent(`studio:editor`, `create-project`, { id });

export const trackEditorClearComposition = (id: string) =>
    trackEvent(`studio:editor`, `clear-composition`, { id });

export const trackLibraryUpload = (type: string) => {
    trackEvent(`studio:library`, `upload-file`, {
        type
    });
};

export const trackTrimCampaignSelected = (contextId: string) => {
    trackEvent(`studio:trim`, `campaign-select`, {
        "context-id": contextId
    });
};

export const trackTrimSetThumbnail = () =>
    trackEvent(`studio:trim`, `thumbnail-set`);

export const trackAspectRatioSelected = (aspectRatio = `16:9`) =>
    trackEvent(`studio:trim`, `select-aspect-ratio-${aspectRatio}`);

export const trackMediaTypeSelected = (mediaType = `clip`) =>
    trackEvent(`studio:trim`, `select-media-type-${mediaType}`);

export const trackOutputFpsSelected = (fps: string) =>
    trackEvent(`studio:trim`, `select-output-fps`, { fps });

export const trackOutputSpeedSelected = (speed: string) =>
    trackEvent(`studio:trim`, `select-output-speed`, { speed });

export const trackOutputDirectionSelected = (direction: string) =>
    trackEvent(`studio:trim`, `select-output-direction`, {
        direction
    });

export const trackCropPadSelected = (cropPad: string) =>
    trackEvent(`studio:trim`, `select-crop-pad`, {
        "crop-property": cropPad
    });

export const enterMouseTrackingMode = () =>
    trackEvent(`studio:trim`, `mouse-tracking-mode`);

export const enterOverlayMode = () => trackEvent(`studio:trim`, `overlay-mode`);

export const mlcModeEnabled = (numStreams: number) =>
    trackEvent(`studio:trim:mlc`, `mlcModeEnabled`, {
        numStreams
    });

export const mlcLayoutChanged = (layout: string) =>
    trackEvent(`studio:trim:mlc`, `mlcLayout`, { layout });

export const mlcHoverView = (buttonName: string) =>
    trackEvent(`studio:trim:mlc`, `mlcHoverViewButton`, {
        buttonName
    });

/*
 * Tracking across the platform
 */

// we pass 'null' for videoCategories if monetization is custom
export const trackTwitterMonetization = (
    type: string,
    videoCategories?: string[] | null
) =>
    trackEvent(`studio`, `twitter-monetization`, {
        type,
        videoCategories
    });

export const trackTwitterMonetizationGeoTargeting = (
    type: string,
    countryCodes: string[]
) =>
    trackEvent(`studio`, `twitter-monetization-geo-targeting`, {
        type,
        countryCodes
    });

export const trackTwitterMonetizationExpirationDate = (
    type: string,
    expirationDate: number
) =>
    trackEvent(`studio`, `twitter-monetization-expiration-date`, {
        type,
        expirationDate
    });

export const trackTwitterMonetizationInStreamAdsTemplateCreate = (
    templateId: string
) =>
    trackEvent(`studio`, `twitter-monetization-in-stream-ads-template-create`, {
        templateId
    });

export const trackTwitterMonetizationInStreamAdsTemplateUse = (
    templateId: string
) =>
    trackEvent(`studio`, `twitter-monetization-in-stream-ads-template-use`, {
        templateId
    });

export const trackTrimDestinationEditor = (destinations: string[]) => {
    if (destinations.includes("campaign") && destinations.includes("editor")) {
        trackEvent(`studio`, `trim-destination-campaign-editor`);
    } else if (destinations.includes("editor")) {
        trackEvent(`studio`, `trim-destination-editor`);
    }
};

/*
 * EPG tracking
 */

export const trackEpgEventCreation = (
    streamId: string,
    eventName: string,
    eventId: string
) =>
    trackEvent(`studio:epg`, `create-EPG-event`, {
        streamId,
        eventName,
        eventId
    });

export const trackEpgEventView = (
    streamId: string,
    eventName: string,
    eventId: string
) =>
    trackEvent(`studio:epg`, `view-EPG-event`, {
        streamId,
        eventName,
        eventId
    });

export const trackLibraryVideoCardClick = (shareId: string, grabId: string) =>
    trackEvent(`studio:library`, `video-card-click`, {
        "share-id": shareId,
        "grab-id": grabId
    });

export const trackLibraryPublishToGallery = (
    published: "true" | "false",
    shareId: string
) => {
    const publishedBoolean = published === `true` ? true : false;
    trackEvent(`studio:library`, `publish-to-gallery`, {
        "is-published": publishedBoolean,
        "share-id": shareId
    });
};

export const trackLibraryShare = (
    grabId: string,
    grabType: string,
    socialAccounts: string[],
    scheduled: boolean,
    hasCreativeSet: string
) =>
    trackEvent(
        `studio:library`,
        hasCreativeSet ? `share-with-creative-set` : `share`,
        {
            grabId,
            grabType,
            socialAccounts,
            scheduled
        }
    );

export const trackLibraryShareMentions = (mention: AnalyticsParameters) =>
    trackEvent(`studio:mention`, `mention`, {
        username: mention.username,
        platform: mention.platform
    });

export const trackLibraryClipDownload = (
    shareId: string,
    aspectRatio: string
) =>
    trackEvent(`studio:library`, `clip-download`, {
        "share-id": shareId,
        "aspect-ratio": aspectRatio
    });

export const trackLibraryClipVodEdit = (shareId: string) =>
    trackEvent(`studio:library`, `clip-vod-edit`, {
        "share-id": shareId
    });

export const trackLibraryClipViewInStream = (grabId: string) =>
    trackEvent(`studio:library`, `view-in-stream`, {
        "grab-id": grabId
    });

export const trackLibraryShowShareHistory = () =>
    trackEvent(`studio:library`, `show-share-history`);

export const trackLibrarySeePostBtn = (url: string) =>
    trackEvent(`studio:library`, `see-post-button-click`, {
        url
    });

export const trackLibraryDeletePost = (
    url: string,
    credentialType: string,
    shareId: string,
    userSocialCredentialId: string
) =>
    trackEvent(`studio:library`, `delete-post`, {
        url,
        "credential-type": credentialType,
        "share-id": shareId,
        "credential-id": userSocialCredentialId
    });

export const trackLibraryClipDeletion = (grabId: string) => {
    trackEvent(`studio:library`, `delete-clip`, {
        grabId
    });
};

/*
 * Creative Sets tracking
 */

export const trackCreativeSetsOpenManager = () => {
    trackEvent(`studio:creativesets`, `open-manager`);
};

export const trackCreativeSetsSelectCreativeSet = (creativeSetId: string) =>
    trackEvent(`studio:creativesets`, `select-creative-set`, {
        creativeSetId
    });

export const trackCreativeSetsDeleteCreativeSet = (creativeSetId: string) =>
    trackEvent(`studio:creativesets`, `delete-creative-set`, {
        creativeSetId
    });

export const trackCreativeSetsViewCreativeSet = (creativeSetId: string) => {
    trackEvent(`studio:creativesets`, `view-creative-set`, {
        "view-creative-set": true,
        creativeSetId
    });
};

export const trackCSAdvancedClick = () => {
    trackEvent(`studio:creativesets`, `select-advanced-new-creative-set`);
};

export const trackCreativeSetsCreateNewCreativeSet = (
    defaultPreBumperAdded: boolean,
    defaultPostBumperAdded: boolean,
    twitterCtaType?: string | null,
    aspectRatio?: string
) => {
    const data = {
        defaultPreBumperAdded,
        defaultPostBumperAdded,
        twitterCtaType,
        aspectRatio
    };
    trackEvent(`studio:creativesets`, `create-new-creative-set`, data);
    trackNewCreativeSetAspectRatio(aspectRatio, data);
};

export const trackNewCreativeSetAspectRatio = (
    aspectRatio: string,
    data: AnalyticsParameters
) =>
    trackEvent(
        `studio:creative-sets`,
        `create-new-creative-set-${aspectRatio}`,
        data
    );

export const trackCreativeSetsCreateNewCreativeSetWithBrandedContentOverlay =
    () => {
        trackEvent(
            `studio:creativesets`,
            `create-new-creative-set-branded-content-overlay`
        );
    };

export const trackCreativeSetsCreateNewCreativeSetWithBrandedContentSponsor = (
    sponsorId: string
) => {
    trackEvent(
        `studio:creativesets`,
        `create-new-creative-set-branded-content-overlay`,
        {
            sponsorId
        }
    );
};

/*
 * Project select tracking
 */
export const trackProjectSelectOpen = (projectName: string) => {
    trackEvent(`studio:project-select`, `openProjectSelect`, {
        projectName
    });
};

export const trackProjectSelectSearch = (searchQuery: string) => {
    trackEvent(`studio:project-select`, `searchProjectSelect`, {
        searchQuery
    });
};

/*
 * Cloud producer tracking
 */

export const trackCloudProducerAddOtherSource = (
    layerIndex: number,
    type: string
) => {
    trackEvent(`studio:cloudproducer`, `source-other-add`, {
        layerIndex,
        type
    });
};

export const trackCloudProducerAddIntegration = (integration: string) => {
    trackEvent(`studio:cloudproducer:`, `add-integration-${integration}`);
};

export const trackCloudProducerCommitStream = (
    transitionType: string,
    duration: number
) => {
    trackEvent(`studio:cloudproducer`, `stream-commit`, {
        transitionType,
        duration
    });
};

export const trackCloudProducerLoadingWait = (time: number) => {
    trackEvent(`studio:cloudproducer`, `producer-loading-duration`, {
        time
    });
};

/*
 * FTP Tracking
 */
export const trackFTPusage = (shareId: string) => {
    trackEvent(`studio`, `ftp-request`, { shareId });
};

/*
 * User profile tracking
 */

export const trackUserProfileAddSocialCredential = () => {
    trackEvent(`studio:userprofile`, `add-social-credential`);
};

export const trackAddCampaignOpen = () => {
    trackEvent(`studio:campaign`, `add-campaign-open`);
};

export const trackAddCampaignClose = () => {
    trackEvent(`studio:campaign`, `add-campaign-close`);
};

export const trackAddCampaignCreation = (formValues: AnalyticsParameters) => {
    trackEvent(`studio:campaign`, `add-campaign-success`, formValues);
};

/*
 * Automation tracking
 */

export const trackAutomationViewDetails = (id: string) => {
    trackEvent(`studio:automation`, `view-details`, { id });
};

export const trackAutomationDelete = (id: string) => {
    trackEvent(`studio:automation`, `delete`, { id });
};

export const trackAutomationDatasourceAdd = () => {
    trackEvent(`studio:automation`, `datasource-add`);
};

export const trackAutomationDatasourceSelect = (id: string) => {
    trackEvent(`studio:automation`, `datasource-select`, { id });
};

export const trackAutomationSportSelect = (sport: string) => {
    trackEvent(`studio:automation`, `sport-select`, { sport });
};

export const trackAutomationCompetitionSelect = (competitionId: string) => {
    trackEvent(`studio:automation`, `competition-select`, {
        competitionId
    });
};

export const trackAutomationDateFilterChange = (from: number, to: number) => {
    trackEvent(`studio:automation`, `match-date-filter-change`, {
        from,
        to
    });
};

export const trackAutomationMatchSelect = (matchId: string) => {
    trackEvent(`studio:automation`, `match-select`, { matchId });
};

export const trackAutomationEventsSelect = (events: string[]) => {
    trackEvent(`studio:automation`, `match-events-select`, {
        events
    });
};

export const trackAutomationStreamSelect = (streamId: string) => {
    trackEvent(`studio:automation`, `stream-select`, { streamId });
};

export const trackAutomationCampaignSelect = (campaignId: string) => {
    trackEvent(`studio:automation`, `campaign-select`, {
        campaignId
    });
};

export const trackAutomationScheduled = (
    automationOverview: AnalyticsParameters
) => {
    trackEvent(`studio:automation`, `scheduled`, automationOverview);
};

export const trackStreamFilters = (streamFilters: StreamFilters) =>
    trackEvent(`studio:filters`, `stream-filters`, streamFilters);

/*
 * Consumer facing / studiotimeline.jsp Analytics
 */

/*
 * OVP/Share tacking
 */

export const trackThePlatformCustomFieldsShare = () => {
    trackEvent(`ovp:theplatform`, `share:customFields`);
};

export const trackThePlatformTurnOnCustomFields = () => {
    trackEvent(`ovp:theplatform`, `share:customFieldsOn`);
};

/*
 * Editor tracking
 */

export const trackAutomatedCaptions = (projectId: string) => {
    trackEvent(`studio:editor`, `editor-extractCaptionText`, {
        projectId
    });
};

export const trackTranscriberOpened = (projectId: string) => {
    trackEvent(`studio:editor`, `editor-openTranscriber`, {
        projectId
    });
};

export const trackManualTranscriptions = (projectId: string) => {
    trackEvent(`studio:editor`, `editor-addManualCaption`, {
        projectId
    });
};

export const trackTitling = (templateName: string) => {
    trackEvent(`studio:editor`, `editor-add-new-title`, {
        templateName
    });
};

/*
 * Tagging tracking
 */
export const trackNewTagCreated = (tagName: string) => {
    trackEvent(`search:tagging`, `tagging-add-new-tag`, {
        tagName
    });
};

export const trackTagAdded = (tagName: string) => {
    trackEvent(`search:tagging`, `tagging-add-tag-to-grab`, {
        tagName
    });
};

export const trackTenTagsAdded = () => {
    trackEvent(`search:tagging`, `tagging-10-tags-added-to-grab`);
};

export const trackSearchWithTags = (numberOfTags: number) => {
    trackEvent(`search:tagging`, `tagging-search-with-tags`, {
        numberOfTags
    });
};

/*
 * SCTE Ad Insertion tracking
 */
export const trackSCTEAdInserted = (
    streamId: string,
    organizationId: string,
    breakDuration: number,
    markersOffset: number,
    countdownDuration: number
) => {
    trackEvent(`studio:cloudproducer`, `scte-ad-inserted`, {
        streamId,
        organizationId,
        breakDuration,
        markersOffset,
        countdownDuration
    });
};

export const trackSCTEAdCancelled = (
    streamId: string,
    organizationId: string
) => {
    trackEvent(`studio:cloudproducer`, `scte-ad-cancelled`, {
        streamId,
        organizationId
    });
};
